import { Params } from '@angular/router';
import { Category, ICategoryDataV2 } from '@df/catalog/category/category';
import { IConfigData } from '@df/catalog/config/config.interfaces';
import { Search } from '@df/search/search.interfaces';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsConfig } from '@icms/core/resource/config/icms-config';


declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            volunteer?: 0 | 1;
            general_parent?: 0 | 1;
            squirrels_parent?: 0 | 1;
            beavers_parent?: 0 | 1;
            cubs_parent?: 0 | 1;
            scouts_parent?: 0 | 1;
        }
    }
}

declare module '@df/search/search.interfaces' {
    namespace Search {
        interface IRequestOptions {
            element?: string;
        }

        interface IRequest {
            // search request is from resolver service
            resolver?: boolean;
            queryParams?: Params;
        }

        interface IResult {
            // query params for redirect
            queryParams?: Params;
        }
    }
}
export namespace AdvancedCommerce {
    export const ID = 'advanced-commerce';

    /**
     * **********************************************************************************************
     *
     * Events
     */
    export enum Events {
        category = 'category',
        categoryPreload = 'categoryPreload',
        filters = 'filters',
        loadMore = 'loadMore',
        searchResult = 'searchResult'
    }

    export interface EventFilters {
        term?: string;
        category?: Category<ICategoryDataV2>;
    }

    /**
     * **********************************************************************************************
     *
     * Engine
     */
    export namespace Engine {
        export enum Id {
            advancedCommerce = 'advancedCommerce',
            recommendations = 'advancedCommerceRecommendations'
        }

        export namespace Recommendations {
            export interface Options {
                elements: string | string[];
                context?: Api.Context;
            }
        }
    }

    /**
     * **********************************************************************************************
     *
     * CMS config
     */
    export namespace Config {
        export interface Content {
            enabled: boolean;
            domainKey: string;
            locationKey?: string;
            apiKey: string;
            apiSecret: string;
            defaultCategoryId: string;
            customCategoriesId?: ICategoriesId[];
            languageId?: string;
            warehouseId?: string;
        }

        export interface ICategoriesId {
            id: string;
            categories: number[];
        }

        export type Data = IConfigData<Content>;

        export type CmsConfig = ICmsConfig<Config.Data>;
    }

    /**
     * **********************************************************************************************
     *
     * API
     */
    export namespace Api {
        export enum Type {
            query = 'query',
            track = 'track'
        }

        export interface Params {
            [key: string]: string | number | boolean;
        }

        export interface User {
            uid: string; // (Request) A `string` containing a unique id associated with the current user.
            sid: string; // (Request) A `string` containing a unique id associated with the current user session.
            ip_address?: string; // (Request) A `string` containing the IP address associated with the current user session.
            ua?: string; // (Response) A `string` containing the web browser user agent.
        }

        export interface Context {
            category_id?: string; // The id of the category for the current page.
            product_id?: string; // The id of the product for the current product detail page.
            page_id?: string; // The id of the content page for the current page.
            page_url?: string; // The full url corresponding to the web page that the end user is currently viewing.
            user_agent?: string; // The web browser user agent.
            language_id?: string; // This is only required if multiple languages are defined.
            currency_id?: string; // This is only required if multiple currencies are defined.
            search_text?: string; // The search text used in the current search.
            customer_group?: string; // The customer group for the current user.
            click_code?: string;
        }

        export interface ResponseHeader {
            status: number; // A `number` containing the status of the request. Typically these will be 200 for a successful request and 500 for an error
            language: string; // A `string` containing the language used during the processing of the query. If a `language` was sent in the request this should match that value.
            currency: string; // A `string` containing the currency used during the processing of the query. If a `currency` was sent in the request this should match that value.
            redirect_url?: string; // A `string` containing the URL to redirect the user to.
        }

        export interface Pagination {
            ps: number; // The number of items per page.
            pg: number; // The current page number.
        }

        export type Response = Query.Response | Track.Response;
    }

    /**
     * **********************************************************************************************
     *
     * Query
     */
    export namespace Query {
        export enum Element {
            autocomplete = 'autocomplete',
            category = 'category',
            search = 'search'
        }

        export interface RequestData {
            // A `string` or `number` containing a unique identifier.
            //
            // This is an optional value that you provide to the API. The API will return this value back with the response.
            request_id?: string | number;
            // A `string` defining the format of the content returned.
            //
            // The allowed values are "html" or "json". If format is not specified then a default value of "json" is used.
            format?: 'html' | 'json';
            // An `array` with a list of element identifiers.
            //
            // Each element identifier must correspond to those elements defined within the GrapheneHC console.
            elements: string[];
            // An `object` containing key information about the current user.
            //
            // These include:
            //
            // `uid` (Required): A `string` containing a unique id associated with the current user.
            // `sid` (Required): A `string` containing a unique id associated with the current user session.
            // `ip_address` (Optional): A `string` containing the IP address associated with the current user session.
            user: Api.User;
            // An `object` containing key information about the current request.
            //
            // These include:
            //
            // `page_url` (Optional): The full url corresponding to the web page that the end user is currently viewing.
            // `use_agent` (Optional): The web browser user agent.
            // `category_id` (Optional): The id of the category for the current page.
            // `product_id` (Optional): The id of the product for the current product detail page.
            // `page_id` (Optional): The id of the content page for the current page.
            // `language_id` (Optional): This is only required if multiple languages are defined.
            // `currency_id` (Optional): This is only required if multiple currencies are defined.
            context: Api.Context;
            // An addition to the standard parameters, `args` allows you to define your own parameters in the form of key-value pairs.
            //
            // Typically these might be user segmentation values, but they could also include additional product or category details.
            //
            // These parameters can then be used to add in conjunction with strategies to product more customized personalization.
            args?: { [key: string]: string | number | boolean };
            // An `object` containing response definitions & options.
            response_options?: {
                // An `object` containing response definitions for fields returned.
                fields?: {
                    // An `array` of strings that define which product fields should be returned in the request.
                    //
                    // It is recommended that in order to minimize response sizes you should only ask for the fields you need.
                    //
                    // The allowed fields are: `id`, `sku`, `stock`, `url`, `titles`, `abstracts`, `media`, `pricing`,
                    // `features`, `variants`, `properties`, `tags`, `created`, `modified`, `published`
                    //
                    // It is recommended that you only request fields that you need.
                    // If "response_options > fields > products" is not defined then all fields will be returned, which may result in a large payload.
                    products?: string[];
                    // An `array` of strings that define which features should be returned.
                    //
                    // This is used in conjunction with the `products` array and will only be applied if "features" is included in the "products" list.
                    //
                    // If `product_features` is not defined then all features will be returned in the response.
                    //
                    // It is recommended that in order to minimize response sizes you should only ask for the features you need.
                    product_features?: string[];
                };
            };
            // Autocomplete request
            include?: string[];
            template?: string;
            search_text?: string;
            previous_searches?: string[];
            recently_viewed?: string[];
            // for load more
            is_paging_request?: boolean;
        }

        export interface Response {
            // A `string` containing a unique identifier.
            //
            // This is a randomly generated unique identifier generated by GrapheneHC to identify the individual request/response.
            response_id: string;
            // An `object` containing a series of informational variables.
            header: Api.ResponseHeader;
            // An `object` containing key information about the current user.
            user: Api.User;
            // An `array` containing a list of elements. Each element is a container for a set of items.
            elements: { [key: string]: ResponseElements };
            settings?: ResponseSettings;
            labels?: ResponseLabels;
            // error
            error?: string;
            status?: number;
        }

        export type ResponseElementType = 'products' | 'categories' | 'pages' | 'popular_searches';
        export type ResponseItemType = 'product' | 'category' | 'page' | 'advert';

        // An `object` containing all aspects of the items search for this element.
        export type ResponseElement = {
            [key in ResponseElementType]: ResponseResults;
        };

        export interface ResponseElements extends ResponseElement {
            id: string;
            titles?: {
                default?: string;
            };
        }

        export interface ResponseResults {
            facets: ResponseFacet[];
            filters: ResponseFilters;
            // An `object` containing key-value pairs related to this element, including click_codes, page_size, page_count & various other pieces of meta information.
            meta?: ResponseMeta;
            // A `array` containing a list of product results.
            results: {
                // An `object` containing the product details.
                item: ResponseResultsItem;
            }[];
        }

        export interface ResponseFacet {
            field_type: string;
            id: string;
            properties?: {
                magento_attribute_code: string;
            };
            items?: {
                id: string;
                selected: boolean;
                count: number;
                titles: {
                    default: string;
                };
                media?: {
                    default: {
                        src: string;
                    };
                };
                properties?: {
                    hex: string;
                };
            }[];
            meta?: {
                from: number;
                max: number;
                min: number;
                to: number;
            };
            selected: boolean;
            selected_count?: number;
            titles: {
                default: string;
            };
        }

        export interface ResponseFilters {
            all: {
                id: string;
                type: 'value' | 'range';
                items?: {
                    id: string;
                    from?: number;
                    to?: number;
                }[];
                properties?: {
                    magento_attribute_code: string;
                };
                meta?: {
                    from: number;
                    max: number;
                    min: number;
                    to: number;
                };
            }[];
            clear_all_url: string;
        }

        export interface ResponseResultsItem {
            id: string;
            titles?: {
                default: string;
            };
            media?: {
                image: {
                    src: string;
                };
            };
            advert_type?: string;
            type: ResponseItemType;
            sku?: string;
            url?: string | null;
            pricing?: {
                price?: number; // A `number` representation for the product price.
                was?: number; // A `number` representation for the product was price.
                rrp?: number; // A `number` representation for the product recommended retail price.
            };
            data?: {
                [key: string]: any;
            };
            search_term?: string;
            size?: number;
        }

        export interface ResponseSettings {
            autocomplete: {
                manual_searches: {
                    [key: string]: string;
                };
                help: {
                    [key: string]: string;
                };
            };
            sorting: {
                sort: {
                    [key: string]: string;
                };
            };
        }

        export interface ResponseLabels {
            autocomplete: {
                headings: {
                    [key: string]: string;
                };
                no_results: {
                    [key: string]: string;
                    title: string;
                };
            };
            plp: {
                sorting: { [key: string]: string };
            };
        }

        export interface Result extends Search.IResult {}

        export interface ResponseMeta {
            advert_count?: number;
            click_code?: string;
            count?: number;
            current_page?: number;
            from?: number;
            page_count?: number;
            page_size?: number;
            product_count?: number;
            to?: number;
        }

        export interface ResultMetaFilter {
            count: number;
            id: string;
            label: string;
            options: { [key: string]: ResultMetaFilterOption };
            position: number;
            type: string;
        }

        export interface ResultMetaFilterOption {
            count: number;
            id: string;
            label: string;
            position: number;
        }
    }

    /**
     * **********************************************************************************************
     *
     * Track
     */
    export namespace Track {
        export enum Scope {
            view = 'view',
            basket = 'basket',
            order = 'order'
        }

        export enum ActionBasket {
            add = 'add',
            delete = 'delete'
        }

        export enum ActionOrder {
            add = 'add',
            cancel = 'cancel',
            update = 'update',
            return = 'return'
        }

        export type Action = ActionBasket | ActionOrder;

        export interface DataBasket {
            product_id: string;
            quantity: number;
        }

        export interface DataOrder {
            order_id: string;
            order_total: number;
            order_tax?: number;
            order_shipping?: number;
            order_currency: string;
            order_lines: DataOrderLine[];
        }

        export type Data = DataBasket | DataBasket[] | DataOrder;

        export interface DataOrderLine {
            product_id: string;
            product_sku: string;
            product_name: string;
            variant_id?: string;
            variant_sku?: string;
            quantity: number;
            unit_rrp?: number; // if available (includes tax)
            order_line_total: number; // includes tax
        }

        export interface DataView {
            product_id: string;
        }

        export interface RequestData<T = Data> {
            // A `string` or `number` containing a unique identifier.
            //
            // This is an optional value you that provide to the API. The API will return this value back with the response.
            request_id?: string | number;
            // A `string` that defines the type of tracking event being recorded.
            //
            // The allowed values are: "view", "basket", "order"
            scope: Scope;
            // This `string` that defines the type of tracking event being recorded.
            //
            // The allowed values are depend on the event:
            //
            // `basket`: "add", "delete"
            // `order`: "add", "cancel", "update", "return"
            action?: Action;
            //A `object` which contains information specific to the current event.
            //
            // The allowed values are depend on the event:
            //
            // `basket`: "product_id", "quantity"
            // `order`: "order_id", "order_total", "order_currency",
            //          "order_lines" (see {@link https://graphenehc-developer-portal.advancedcommerce.io/docs/integration/api/tracking/examples#orders|examples})
            data: T;
            // An `object` containing key information about the current user.
            //
            // These include:
            //
            // `uid` (Required): A `string` containing a unique id associated with the current user.
            // `sid` (Required): A `string` containing a unique id associated with the current user session.
            // `ip_address` (Optional): A `string` containing the IP address associated with the current user session.
            user: Api.User;
            // An `object` containing key information about the current request.
            //
            // The context object required for tracking is the same as for queries.
            // For more details see: {@link https://graphenehc-developer-portal.advancedcommerce.io/docs/integration/api/query/ref|Query API}.
            context: Api.Context;
        }

        export interface Response {
            status: number;
            event_id?: string;
            message?: string;
        }
    }

    /**
     * **********************************************************************************************
     *
     * Recommendations
     */
    export namespace Recommendations {
        export interface ComponentData extends NICmsContentComponent.IData {
            sections: Section[];
            source: Source;
            limit: number;
        }

        export interface Section {
            identifier: string;
            title?: string;
        }

        export enum Source {
            none = 'none',
            product = 'product',
            basket = 'basket',
            minibasket = 'minibasket'
        }

        export interface Sections {
            identifier: string;
            title: string;
            clickCode?: string;
            ids: number[];
        }

        export const CONFIG: Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'> = {
            id: ID,
            name: 'Advanced Commerce Recommendations',
            tags: ['related', 'recommendations', 'listing', 'products', 'carousel'],
            fields: [
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'sections',
                    title: 'Sections',
                    itemName: 'Section',
                    itemNameField: 'identifier',
                    defaultSize: 1,
                    fields: [
                        <NICmsContentField.ITextField>{
                            key: 'identifier',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Rule identifier',
                            required: true
                        },
                        <NICmsContentField.ITextField>{
                            key: 'title',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Default title',
                            description: 'Default title for the section'
                        }
                    ]
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'source',
                    title: 'Source',
                    required: true,
                    options: [
                        {
                            key: Source.none,
                            title: 'None'
                        },
                        {
                            key: Source.product,
                            title: 'Product'
                        },
                        {
                            key: Source.basket,
                            title: 'Basket'
                        },
                        {
                            key: Source.minibasket,
                            title: 'Mini Basket'
                        }
                    ]
                },
                <NICmsContentField.INumberField>{
                    key: 'limit',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Max products qty',
                    required: true,
                    default: 8
                }
            ]
        };
    }

    /**
     * **********************************************************************************************
     *
     * Helpers
     */
    export type Request = Query.RequestData | Track.RequestData;
}
